import { useState, useEffect, useMemo } from "react";
import List from "../../../site/list";
import { masterListApi, newRecord, tableFields, uriBasePath } from "./module";

const InvoiceList = () => {
  const [sortedData, setSortedData] = useState(null);
  const [filteredData, setFilteredData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Helper function to format date to dd/mm/yyyy - removes time completely
  const formatDate = (dateString) => {
    if (!dateString) return "";
    
    try {
      // First, remove any time components like ", 00:00" or " 00:00"
      let cleanedDate = String(dateString).trim();
      cleanedDate = cleanedDate.replace(/,\s*\d{2}:\d{2}(:\d{2})?.*$/g, '');
      cleanedDate = cleanedDate.replace(/\s+\d{2}:\d{2}(:\d{2})?.*$/g, '');
      
      // If already in dd/mm/yyyy format after cleaning, return it
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(cleanedDate)) {
        return cleanedDate;
      }
      
      // Try to parse as ISO date
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return cleanedDate || "";
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      
      return `${day}/${month}/${year}`;
    } catch (error) {
      // Fallback: just remove time portions
      return String(dateString).replace(/,\s*\d{2}:\d{2}(:\d{2})?.*$/g, '').replace(/\s+\d{2}:\d{2}(:\d{2})?.*$/g, '').trim();
    }
  };

  // Helper function to check if a field is a date field
  const isDateField = (key) => {
    if (!key) return false;
    const dateFieldNames = [
      'date', '_date', 'inv_date', 'add_date', 'update_date', 'due_date', 
      'approval_date', 'created_at', 'updated_at', 'from_date', 'start_date',
      'to_date', 'end_date', 'invoice_date', 'ack_date', 'bt_date', 'deleted_date'
    ];
    
    const lowerKey = key.toLowerCase();
    return dateFieldNames.some(field => 
      lowerKey === field || lowerKey.includes(field)
    );
  };

  // Helper function to format all date fields in the data
  const formatDataDates = (data) => {
    if (!Array.isArray(data)) return data;
    
    return data.map(invoice => {
      const formattedInvoice = {};
      
      // Iterate through all keys and format date fields
      Object.keys(invoice).forEach(key => {
        const value = invoice[key];
        
        // Check if this field should be formatted as a date
        if (value && isDateField(key)) {
          formattedInvoice[key] = formatDate(value);
        } else {
          formattedInvoice[key] = value;
        }
      });
      
      return formattedInvoice;
    });
  };

  // Create modified table fields with custom render functions for date fields
  const modifiedTableFields = useMemo(() => {
    if (!tableFields) return tableFields;
    
    console.log('Original tableFields:', tableFields);
    
    const modified = tableFields.map(field => {
      // Get the field identifier - try multiple properties
      const fieldKey = field.key || field.name || field.field || field.dataIndex || field.accessor || '';
      
      // Check if this field is a date field
      if (fieldKey && isDateField(fieldKey)) {
        console.log('Adding date formatter to field:', fieldKey);
        return {
          ...field,
          render: (value, record) => {
            if (!value) return '';
            const formatted = formatDate(value);
            console.log(`Formatting ${fieldKey}:`, value, '->', formatted);
            return formatted;
          },
          // Also add a formatter property in case the List component uses it
          formatter: (value) => {
            if (!value) return '';
            return formatDate(value);
          }
        };
      }
      return field;
    });
    
    console.log('Modified tableFields:', modified);
    return modified;
  }, []);

  useEffect(() => {
    const fetchAndSortInvoices = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem("zTaskAuthToken");
        
        const baseApi = masterListApi.split('?')[0];
        
        const response = await fetch(baseApi, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch invoices");
        }

        const data = await response.json();

        const sorted = data.sort((a, b) => {
          const addDateA = a.add_date ? new Date(a.add_date).getTime() : 0;
          const updateDateA = a.update_date ? new Date(a.update_date).getTime() : 0;
          const maxDateA = Math.max(addDateA, updateDateA);

          const addDateB = b.add_date ? new Date(b.add_date).getTime() : 0;
          const updateDateB = b.update_date ? new Date(b.update_date).getTime() : 0;
          const maxDateB = Math.max(addDateB, updateDateB);

          return maxDateB - maxDateA;
        });

        // Format dates before setting state
        const formattedData = formatDataDates(sorted);
        
        console.log('Formatted data sample:', formattedData[0]);
        
        setSortedData(formattedData);
        setFilteredData(formattedData);
      } catch (error) {
        console.error("Error fetching and sorting invoices:", error);
        setSortedData([]);
        setFilteredData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndSortInvoices();
  }, []);

  // Apply CSS fix after data is rendered
  useEffect(() => {
    if (filteredData) {
      // Add a small delay to ensure DOM is ready
      setTimeout(() => {
        // Find all table cells and remove time from date strings
        const cells = document.querySelectorAll('.invoice-table-wrapper td');
        cells.forEach(cell => {
          if (cell.textContent && cell.textContent.match(/\d{2}\/\d{2}\/\d{4},\s*\d{2}:\d{2}/)) {
            cell.textContent = cell.textContent.replace(/,\s*\d{2}:\d{2}(:\d{2})?/g, '');
          }
        });
      }, 100);
    }
  }, [filteredData]);

  useEffect(() => {
    if (!sortedData) return;

    if (!searchTerm.trim()) {
      setFilteredData(sortedData);
      return;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    
    const filtered = sortedData.filter((invoice) => {
      // Search through all fields
      return Object.values(invoice).some(value => {
        if (value == null) return false;
        return String(value).toLowerCase().includes(lowerSearchTerm);
      });
    });

    setFilteredData(filtered);
  }, [searchTerm, sortedData]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  if (isLoading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingMessage}>Loading invoices...</div>
      </div>
    );
  }

  return (
    <div className="invoice-list-wrapper" style={styles.pageContainer}>
      {/* Search Box - Fixed at top */}
      <div style={styles.searchContainer}>
        <div style={styles.searchBoxWrapper}>
          <input
            type="text"
            placeholder="Search invoices (Doc No, Status, Amount, etc.)..."
            value={searchTerm}
            onChange={handleSearchChange}
            style={styles.searchInput}
          />
          {searchTerm && (
            <button
              onClick={clearSearch}
              style={styles.clearButton}
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>
        {searchTerm && (
          <div style={styles.searchResults}>
            Found {filteredData?.length || 0} invoice(s)
          </div>
        )}
      </div>

      {/* Table Container - Scrollable with sticky header */}
      <div className="invoice-table-wrapper" style={styles.tableOuterContainer}>
        <List
          listApi={masterListApi}
          tableFields={modifiedTableFields}
          listHeading={"Invoices"}
          newRecord={newRecord}
          uriBasePath={uriBasePath}
          preloadedData={filteredData}
          skipDataFetch={true}
        />
      </div>
    </div>
  );
};

// Inline Styles
const styles = {
  pageContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    maxHeight: "calc(100vh - 180px)",
    overflow: "hidden",
    boxSizing: "border-box",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "400px",
  },
  loadingMessage: {
    fontSize: "18px",
    color: "#6c757d",
    fontWeight: "500",
  },
  searchContainer: {
    flexShrink: 0,
    marginBottom: "16px",
    backgroundColor: "#ffffff",
    padding: "16px 20px",
    borderRadius: "8px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  searchBoxWrapper: {
    position: "relative",
    width: "100%",
    maxWidth: "100%",
  },
  searchInput: {
    width: "100%",
    padding: "12px 40px 12px 16px",
    fontSize: "15px",
    border: "2px solid #dee2e6",
    borderRadius: "8px",
    outline: "none",
    transition: "all 0.3s ease",
    boxSizing: "border-box",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  clearButton: {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "transparent",
    border: "none",
    fontSize: "20px",
    color: "#6c757d",
    cursor: "pointer",
    padding: "4px 8px",
    borderRadius: "4px",
    transition: "all 0.2s ease",
  },
  searchResults: {
    marginTop: "12px",
    fontSize: "14px",
    color: "#495057",
    fontWeight: "500",
  },
  tableOuterContainer: {
    flex: 1,
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    overflow: "auto",
    display: "block",
    position: "relative",
    minHeight: 0,
  },
};

// Global styles for the table - CRITICAL STYLING
const globalStyles = `
  /* Wrapper styling */
  .invoice-list-wrapper {
    position: relative;
    width: 100%;
  }

  /* Table wrapper must have overflow */
  .invoice-table-wrapper {
    overflow: auto !important;
    height: 100%;
    position: relative;
  }

  /* Force the List component's table to be scrollable */
  .invoice-table-wrapper > div {
    height: 100%;
    overflow: visible !important;
  }

  .invoice-table-wrapper table {
    width: 100%;
    border-collapse: collapse;
    min-width: 1400px;
    position: relative;
  }

  /* CRITICAL: Sticky header */
  .invoice-table-wrapper thead {
    position: sticky !important;
    top: 0 !important;
    z-index: 10 !important;
    background-color: #f8f9fa !important;
  }

  .invoice-table-wrapper thead tr {
    background-color: #f8f9fa !important;
  }

  .invoice-table-wrapper th {
    padding: 14px 12px !important;
    text-align: left !important;
    font-weight: 600 !important;
    font-size: 14px !important;
    color: #212529 !important;
    background-color: #f8f9fa !important;
    white-space: nowrap !important;
    border-bottom: 2px solid #dee2e6 !important;
    position: sticky !important;
    top: 0 !important;
  }

  .invoice-table-wrapper td {
    padding: 12px !important;
    text-align: left !important;
    font-size: 14px !important;
    color: #495057 !important;
    border-bottom: 1px solid #dee2e6 !important;
    white-space: nowrap !important;
  }

  .invoice-table-wrapper tbody tr:hover {
    background-color: #f1f3f5 !important;
    cursor: pointer !important;
  }

  /* Scrollbar styling - always visible */
  .invoice-table-wrapper::-webkit-scrollbar {
    height: 14px !important;
    width: 14px !important;
  }

  .invoice-table-wrapper::-webkit-scrollbar-track {
    background: #f1f1f1 !important;
    border-radius: 0 !important;
  }

  .invoice-table-wrapper::-webkit-scrollbar-thumb {
    background: #888 !important;
    border-radius: 7px !important;
    border: 2px solid #f1f1f1 !important;
  }

  .invoice-table-wrapper::-webkit-scrollbar-thumb:hover {
    background: #555 !important;
  }

  .invoice-table-wrapper::-webkit-scrollbar-corner {
    background: #f1f1f1 !important;
  }

  /* Force scrollbar to always show */
  .invoice-table-wrapper {
    overflow-x: scroll !important;
    overflow-y: scroll !important;
  }

  /* Remove any margin/padding that could interfere */
  .invoice-table-wrapper * {
    box-sizing: border-box;
  }

  /* Search input focus */
  .invoice-list-wrapper input[type="text"]:focus {
    border-color: #0d6efd !important;
    box-shadow: 0 0 0 0.2rem rgba(13, 111, 253, 0.25) !important;
  }
  
  /* Clear button hover */
  .invoice-list-wrapper button:hover {
    background-color: #e9ecef !important;
    color: #212529 !important;
  }

  /* Override any conflicting styles from List component */
  .invoice-table-wrapper .list-container {
    height: 100% !important;
    overflow: visible !important;
  }

  .invoice-table-wrapper .table-container {
    overflow: visible !important;
  }
`;

// Inject global styles
if (typeof document !== "undefined") {
  const existingStyle = document.getElementById("invoice-list-styles");
  if (existingStyle) {
    existingStyle.remove();
  }
  
  const styleElement = document.createElement("style");
  styleElement.id = "invoice-list-styles";
  styleElement.textContent = globalStyles;
  document.head.appendChild(styleElement);
}

export default InvoiceList;